import React, { useEffect, useState } from 'react'
import { supabase, signInWithSlack } from '../lib/supabase'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, CheckCircle, Lock } from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OAuthAuthorizationDetails {
  authorization_id: string
  client: {
    name: string
    id?: string
    client_id?: string
  }
  /** Fallback location for client_id in some response shapes */
  client_id?: string
  redirect_uri: string
  /** Space-separated scope string, e.g. "openid email profile" */
  scope: string
}

interface OAuthRedirect {
  redirect_url: string
}

interface EligibleTeam {
  id: string
  name: string | null
}

type PageState =
  | { status: 'loading' }
  | { status: 'needs-login' }
  | { status: 'error'; message: string }
  | {
      status: 'ready'
      authDetails: OAuthAuthorizationDetails
      clientId: string
      teams: EligibleTeam[]
      /** null until user picks one (only when teams.length > 1) */
      selectedTeamId: string | null
    }
  | { status: 'deciding'; action: 'approve' | 'deny' }
  | { status: 'redirecting' }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract client_id defensively — the field name varies across Supabase
 * response shapes and may be promoted to the top level or nested under client.
 */
function extractClientId(d: OAuthAuthorizationDetails): string | null {
  return d.client?.id ?? d.client_id ?? (d.client as any)?.client_id ?? null
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const OAuthConsent: React.FC = () => {
  // Read authorization_id once on mount — stable across re-renders
  const authorizationId = new URLSearchParams(window.location.search).get('authorization_id')

  const [state, setState] = useState<PageState>({ status: 'loading' })

  useEffect(() => {
    if (!authorizationId) {
      setState({ status: 'error', message: 'Missing authorization_id in URL.' })
      return
    }

    const init = async () => {
      // ── 1. Session gate ────────────────────────────────────────────────────
      // getAuthorizationDetails throws AuthSessionMissingError without a session,
      // so we check first and redirect to Slack OIDC login if needed.
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session) {
        setState({ status: 'needs-login' })
        return
      }

      // ── 2. Fetch authorization details ────────────────────────────────────
      const { data: authDetails, error: authError } =
        await (supabase.auth as any).oauth.getAuthorizationDetails(authorizationId)

      // Diagnostic log — lets us confirm the real response shape on first render
      console.log('AUTH_DETAILS_RAW', authDetails)

      if (authError || !authDetails) {
        setState({
          status: 'error',
          message: authError?.message ?? 'Invalid or expired authorization request.',
        })
        return
      }

      // ── 3. Already-consented short-circuit ────────────────────────────────
      // When authorization_id is absent the binding already exists; redirect
      // immediately without showing any UI (per the Supabase doc example).
      if (!('authorization_id' in authDetails)) {
        window.location.href = (authDetails as OAuthRedirect).redirect_url
        return
      }

      const details = authDetails as OAuthAuthorizationDetails

      // ── 4. Extract client_id defensively ──────────────────────────────────
      const clientId = extractClientId(details)
      if (!clientId) {
        console.error('Could not resolve client_id from authDetails:', authDetails)
        setState({
          status: 'error',
          message:
            'Could not determine the requesting client. Please contact support.',
        })
        return
      }

      // ── 5. Query eligible teams ───────────────────────────────────────────
      // A team is eligible when the user is a member AND the bot is installed
      // (has_slack_bot_token AND app_uninstalled_at IS NULL). The token
      // itself is not readable from the browser.
      const { data: memberships, error: teamsError } = await supabase
        .from('team_members')
        .select('teams:teams(id, name, has_slack_bot_token, app_uninstalled_at)')
        .eq('auth_user_id', session.user.id)

      if (teamsError) {
        setState({
          status: 'error',
          message: `Failed to load your workspaces: ${teamsError.message}`,
        })
        return
      }

      const eligibleTeams: EligibleTeam[] = (memberships ?? [])
        .map((m: any) => m.teams)
        .filter((t: any) => t && t.has_slack_bot_token && !t.app_uninstalled_at)
        .map((t: any) => ({ id: t.id, name: t.name as string | null }))

      // Pre-select when there's exactly one eligible team
      const selectedTeamId = eligibleTeams.length === 1 ? eligibleTeams[0].id : null

      setState({
        status: 'ready',
        authDetails: details,
        clientId,
        teams: eligibleTeams,
        selectedTeamId,
      })
    }

    init().catch((err: unknown) => {
      console.error('OAuthConsent init error:', err)
      setState({
        status: 'error',
        message:
          err instanceof Error ? err.message : 'An unexpected error occurred.',
      })
    })
  }, [authorizationId])

  // ── Event handlers ──────────────────────────────────────────────────────

  const handleLogin = () => {
    if (!authorizationId) return
    // Redirect back to this page (with authorization_id) after Slack OIDC
    const redirectTo = `${window.location.origin}/oauth/consent?authorization_id=${authorizationId}`
    signInWithSlack(redirectTo)
  }

  const handleApprove = async () => {
    if (state.status !== 'ready' || !authorizationId) return
    const { clientId, selectedTeamId } = state
    if (!selectedTeamId) return

    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (!session) {
      setState({ status: 'error', message: 'Session expired. Reload and try again.' })
      return
    }

    setState({ status: 'deciding', action: 'approve' })

    // ── STEP A: Upsert binding FIRST ─────────────────────────────────────────
    // user_id is the auth.users UUID (session.user.id), NOT public.users.id.
    // The write uses the user's session client so RLS applies.
    const { error: upsertError } = await supabase
      .from('oauth_agent_bindings')
      .upsert(
        { user_id: session.user.id, client_id: clientId, team_id: selectedTeamId },
        { onConflict: 'user_id,client_id' }
      )

    if (upsertError) {
      console.error('oauth_agent_bindings upsert failed:', upsertError)
      setState({
        status: 'error',
        message: `Failed to save workspace binding: ${upsertError.message}`,
      })
      return
    }

    // ── STEP B: Approve ONLY after successful upsert ──────────────────────
    const { data: approveData, error: approveError } = await (
      supabase.auth as any
    ).oauth.approveAuthorization(authorizationId)

    if (approveError || !approveData) {
      setState({
        status: 'error',
        message: approveError?.message ?? 'Authorization approval failed.',
      })
      return
    }

    setState({ status: 'redirecting' })
    window.location.href = approveData.redirect_url
  }

  const handleDeny = async () => {
    if (!authorizationId) return
    setState({ status: 'deciding', action: 'deny' })

    const { data: denyData, error: denyError } = await (
      supabase.auth as any
    ).oauth.denyAuthorization(authorizationId)

    if (denyError || !denyData) {
      setState({
        status: 'error',
        message: denyError?.message ?? 'Failed to deny the authorization.',
      })
      return
    }

    setState({ status: 'redirecting' })
    window.location.href = denyData.redirect_url
  }

  // ── Render ───────────────────────────────────────────────────────────────

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="animate-pulse text-gray-400 text-sm">Loading…</p>
      </div>
    )
  }

  if (state.status === 'needs-login') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Sign in to continue</CardTitle>
            <CardDescription>
              An app is requesting access to your SyncSimple account. Sign in
              with Slack to review the request.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={handleLogin} className="w-full gap-2">
              {/* Slack SVG mark */}
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                viewBox="0 0 54 54"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.712.133a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386h5.376V5.52A5.381 5.381 0 0 0 19.712.133m0 14.365H5.376A5.381 5.381 0 0 0 0 19.884a5.381 5.381 0 0 0 5.376 5.387h14.336a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386"
                  fill="#36C5F0"
                />
                <path
                  d="M53.76 19.884a5.381 5.381 0 0 0-5.376-5.386 5.381 5.381 0 0 0-5.376 5.386v5.387h5.376a5.381 5.381 0 0 0 5.376-5.387m-14.336 0V5.52A5.381 5.381 0 0 0 34.048.133a5.381 5.381 0 0 0-5.376 5.387v14.364a5.381 5.381 0 0 0 5.376 5.387 5.381 5.381 0 0 0 5.376-5.387"
                  fill="#2EB67D"
                />
                <path
                  d="M34.048 54a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386h-5.376v5.386A5.381 5.381 0 0 0 34.048 54m0-14.365h14.336a5.381 5.381 0 0 0 5.376-5.386 5.381 5.381 0 0 0-5.376-5.387H34.048a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386"
                  fill="#ECB22E"
                />
                <path
                  d="M0 34.249a5.381 5.381 0 0 0 5.376 5.386 5.381 5.381 0 0 0 5.376-5.386v-5.387H5.376A5.381 5.381 0 0 0 0 34.249m14.336 0v14.364A5.381 5.381 0 0 0 19.712 54a5.381 5.381 0 0 0 5.376-5.387V34.249a5.381 5.381 0 0 0-5.376-5.387 5.381 5.381 0 0 0-5.376 5.387"
                  fill="#E01E5A"
                />
              </svg>
              Sign in with Slack
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Authorization Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{state.message}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (state.status === 'deciding' || state.status === 'redirecting') {
    const label =
      state.status === 'redirecting'
        ? 'Redirecting…'
        : state.action === 'approve'
        ? 'Approving…'
        : 'Denying…'
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="animate-pulse text-gray-400 text-sm">{label}</p>
      </div>
    )
  }

  // ── Ready state ────────────────────────────────────────────────────────────
  const { authDetails, teams, selectedTeamId } = state
  const scopes = authDetails.scope?.trim()
    ? authDetails.scope.split(' ').filter(Boolean)
    : []
  const canApprove = !!selectedTeamId

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <Lock className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle>Authorize {authDetails.client.name}</CardTitle>
              <CardDescription>
                This app wants to access your SyncSimple account
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* Scopes */}
          {scopes.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Requested permissions
              </p>
              <div className="flex flex-wrap gap-1.5">
                {scopes.map((scope) => (
                  <Badge key={scope} variant="secondary">
                    {scope}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Team / workspace picker */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Slack workspace
            </p>
            {teams.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No eligible Slack workspaces found. Install the SyncSimple bot
                  to a workspace first, then return here.
                </AlertDescription>
              </Alert>
            ) : teams.length === 1 ? (
              <p className="text-sm text-gray-700 bg-gray-100 rounded-md px-3 py-2">
                {teams[0].name ?? teams[0].id}
              </p>
            ) : (
              <Select
                value={selectedTeamId ?? ''}
                onValueChange={(id) =>
                  setState((s) =>
                    s.status === 'ready' ? { ...s, selectedTeamId: id } : s
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a workspace" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name ?? t.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleDeny}
            disabled={state.status !== 'ready'}
          >
            Deny
          </Button>
          <Button
            className="flex-1 gap-2"
            onClick={handleApprove}
            disabled={!canApprove}
          >
            <CheckCircle className="h-4 w-4" />
            Approve
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}

export default OAuthConsent
